export class IOSAudioManager {
  constructor() {
    this.isIOS =
      /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    this.isChromeIOS = this.isIOS && /CriOS/.test(navigator.userAgent);
    this.isUnlocked = false;
    this.audioContext = null;
    this.chromeAudio = null;
    this.activeHtmlAudio = null;
    this.currentAudioBuffer = null;
    this.currentAudioSource = null;
  }

  async getAudioContext() {
    if (!this.audioContext || this.audioContext.state === 'closed') {
      const Ctor = window.AudioContext || window.webkitAudioContext;
      this.audioContext = new Ctor();
    }
    if (this.audioContext.state === 'suspended') {
      await this.audioContext.resume();
    }
    return this.audioContext;
  }

  // Must be called inside user gesture to unlock both AudioContext and HTMLAudio on Chrome iOS
  async initializeFromUserGesture() {
    if (this.isUnlocked) {
      return { success: true, audioContext: this.audioContext };
    }
    try {
      const ctx = await this.getAudioContext();
      if (ctx.state === 'suspended') await ctx.resume();

      // prime context with silent buffer
      const buffer = ctx.createBuffer(1, 1, ctx.sampleRate);
      const src = ctx.createBufferSource();
      src.buffer = buffer;
      src.connect(ctx.destination);
      src.start(0);
      await new Promise(r => setTimeout(r, 50));

      // Pre-create and play a muted HTMLAudioElement inside the user gesture to comply with
      // iOS autoplay policies. Chrome on iOS requires re-using the SAME element later to keep
      // the unlocked state, whereas Safari does NOT – we can dispose of the element after
      // priming the policy. Therefore we branch the logic and only persist the element for
      // Chrome iOS.

      const unlockAudio = new Audio();
      unlockAudio.preload = 'auto';
      unlockAudio.muted = true;
      // A tiny silent data-URI (~0.02 s) works better than an empty src on some iOS versions
      unlockAudio.src =
        'data:audio/mpeg;base64,//uQxAAAAAAAAAAAAAAAAAAAAAAAWGluZwAAAA8AAAACAAACcQCAeHgAAAC4AQACABAAZGR0YQAAAAA=';

      await unlockAudio.play().catch(() => {
        /* Some versions will reject even muted play; unlocking still occurs – swallow error */
      });
      unlockAudio.pause();

      // Keep a reference so we can reuse the unlocked element later for playback. This is
      // mandatory for Chrome iOS and helps Safari avoid additional autoplay checks.
      this.chromeAudio = unlockAudio;

      this.isUnlocked = true;
      return { success: true, audioContext: ctx };
    } catch (err) {
      this.isUnlocked = false;
      return {
        success: false,
        error: err instanceof Error ? err.message : String(err),
      };
    }
  }

  setupEndedListener(audio, onEnded) {
    const handleEnded = () => {
      onEnded();
      audio.removeEventListener('ended', handleEnded);
    };
    audio.addEventListener('ended', handleEnded);

    // Safety timer logic for HTMLAudioElement
    const bufferSeconds = 2;
    const setSafetyTimeout = duration => {
      if (!isFinite(duration) || duration <= 0) return;
      const timeoutMs = (duration + bufferSeconds) * 1000;
      setTimeout(() => {
        if (!audio.paused && !audio.ended) {
          onEnded();
        }
      }, timeoutMs);
    };

    if (audio.readyState >= 1 && audio.duration) {
      setSafetyTimeout(audio.duration);
    } else {
      const onLoadedMeta = () => {
        setSafetyTimeout(audio.duration);
        audio.removeEventListener('loadedmetadata', onLoadedMeta);
      };
      audio.addEventListener('loadedmetadata', onLoadedMeta);
    }
  }

  async playAudio(audioUrl, callbacks) {
    if (!this.isUnlocked) {
      return {
        success: false,
        error: 'Audio not unlocked. Call initializeFromUserGesture() first.',
      };
    }

    const { onPlay, onEnded } = callbacks;

    // Stop any existing playback before starting a new one
    this.stopAudio();

    if (this.isChromeIOS && this.chromeAudio) {
      this.activeHtmlAudio = this.chromeAudio;
    } else if (this.isIOS) {
      this.activeHtmlAudio = this.chromeAudio ?? new Audio();
      if (!this.chromeAudio) this.chromeAudio = this.activeHtmlAudio;
    }

    if (this.activeHtmlAudio) {
      const audio = this.activeHtmlAudio;
      audio.src = audioUrl;
      audio.muted = false;
      audio.preload = 'auto';

      this.setupEndedListener(audio, onEnded);

      try {
        await audio.play();
        onPlay?.();
        return { success: true };
      } catch (err) {
        onEnded(); // Ensure state is cleaned up on failure
        return {
          success: false,
          error: err instanceof Error ? err.message : String(err),
        };
      }
    }

    // Fallback to Web-Audio buffer for other iOS environments
    try {
      const ctx = this.audioContext;
      const resp = await fetch(audioUrl);
      if (!resp.ok) throw new Error(`Fetch failed: ${resp.status}`);
      const arrayBuffer = await resp.arrayBuffer();
      const audioBuffer = await ctx.decodeAudioData(arrayBuffer);
      const src = ctx.createBufferSource();
      src.buffer = audioBuffer;
      src.connect(ctx.destination);
      this.currentAudioSource = src;

      src.onended = () => {
        if (this.currentAudioSource === src) {
          this.currentAudioSource = null;
        }
        onEnded();
      };

      src.start(0);
      onPlay?.();

      // Safety timeout for Web Audio API playback.
      const durationMs = audioBuffer.duration * 1000;
      setTimeout(() => {
        // If source is still active, it means 'onended' didn't fire.
        if (this.currentAudioSource === src) {
          this.stopAudio(); // this will trigger onEnded
        }
      }, durationMs + 2000); // 2s buffer

      return { success: true };
    } catch (err) {
      onEnded(); // Ensure state is cleaned up on failure
      return {
        success: false,
        error: err instanceof Error ? err.message : String(err),
      };
    }
  }

  stopAudio() {
    if (this.currentAudioSource) {
      // Unhook the onended handler before stopping to prevent it from firing.
      // The calling context (e.g., a new playAudio call) will handle state changes.
      this.currentAudioSource.onended = null;
      this.currentAudioSource.stop();
      this.currentAudioSource.disconnect();
      this.currentAudioSource = null;
    }
    if (this.activeHtmlAudio) {
      this.activeHtmlAudio.pause();
      this.activeHtmlAudio.src = ''; // Detach source
    }
  }
}

export const iosAudioManager = new IOSAudioManager();
